import { useEffect, useCallback, useRef } from "react";
import { wsClient, type WsPricesMessage, type WsMessage } from "@/backend/ws-client";
import { usePriceStore } from "@/backend/store";
import { useMarkets } from "@/domains/markets";
import { debug } from "@/lib/debug";

const USE_WEBSOCKET = import.meta.env.VITE_USE_WEBSOCKET !== "false";

/**
 * Hook to sync oracle prices from the backend via WebSocket.
 * 
 * Industry-standard pattern (like Binance, HyperLiquid):
 * - WebSocket prices go directly into Zustand store
 * - NO React state updates here (avoids component re-renders)
 * - Table cells subscribe to individual prices via selectors
 * 
 * Now supports dynamic markets from API (not just hardcoded MARKETS).
 */
export const useOracleSync = () => {
  // Get stable reference to store action for oracle prices (doesn't cause re-renders)
  const setOraclePrices = usePriceStore.getState().setOraclePrices;
  
  // Get dynamic markets list from API
  const { markets } = useMarkets();
  
  // Keep a Set of valid market symbols for O(1) lookup
  // Use ref to avoid re-creating callback when markets change
  const validMarketsRef = useRef<Set<string>>(new Set());
  
  // Update valid markets set when markets change
  useEffect(() => {
    validMarketsRef.current = new Set(markets.map(m => m.symbol));
    debug.log('OracleSync', `Updated valid markets: ${validMarketsRef.current.size} markets`);
  }, [markets]);

  const handlePricesMessage = useCallback((message: WsMessage) => {
    // Only process 'prices' type messages
    if (message.type !== "prices") return;
    
    const pricesMsg = message as WsPricesMessage;
    const prices = pricesMsg.prices;
    
    if (!prices || prices.length === 0) return;

    // Collect price updates for Zustand store
    const priceUpdates: Array<{ symbol: string; price: number }> = [];
    
    // Update oracle prices for each market
    for (const priceData of prices) {
      const market = priceData.market;
      
      // Accept all markets from backend - the backend only sends valid markets
      // This allows dynamic markets to work without hardcoded checks
      // The backend is the source of truth for which markets exist

      const price = parseFloat(priceData.price);
      if (isNaN(price) || price <= 0) continue;

      // Note: Removed localStorage update from hot path for performance
      // localStorage writes are synchronous and block the main thread
      // The Zustand store is the source of truth for real-time oracle prices
      
      // Collect for Zustand batch update
      priceUpdates.push({ symbol: market, price });
    }

    // Batch update Zustand oracle price store (not market prices!)
    // This is the key: only cells subscribed to changed prices will re-render
    if (priceUpdates.length > 0) {
      setOraclePrices(priceUpdates);
    }
  }, [setOraclePrices]);

  useEffect(() => {
    if (!USE_WEBSOCKET) {
      // Fall back to legacy HTTP polling if WebSocket is disabled
      debug.log('OracleSync', 'WebSocket disabled, using HTTP fallback');
      return;
    }

    debug.log('OracleSync', 'Subscribing to WebSocket prices channel');
    
    // Subscribe to the all-prices WebSocket channel
    const unsubscribe = wsClient.subscribe("prices", handlePricesMessage);

    // Check connection state periodically
    const checkConnection = () => {
      // Skip state updates during debugging to avoid re-render storms
    };
    
    checkConnection();
    const interval = setInterval(checkConnection, 1000);

    return () => {
      debug.log('OracleSync', 'Unsubscribing from WebSocket prices channel');
      unsubscribe();
      clearInterval(interval);
    };
  }, [handlePricesMessage]);

  return {
    isConnected: true,
    priceCount: 0,
  };
};
